// @ts-nocheck
import React, { useEffect, useMemo, useRef, useState } from 'react'
import GoogleMapReact from 'google-map-react'
import styled from 'styled-components/macro'
import { getTranslatedTextByKey, getGeoLocationPermissionState, requestMyLocation } from 'utils/utils'
import InputAdornment from '@material-ui/core/InputAdornment'
import GooglePlacesAutoCompleteNew from 'components/common/GooglePlacesAutoCompleteNew'
import Marker from './Marker'
import { inject, observer } from 'mobx-react'
import { useLocationMobileApp } from 'hooks/useLocationMobileApp'
import { CONSTANTS, ORDER_TYPES } from 'utils/constants'
import Divider from '@material-ui/core/Divider'
import TextFieldBase from 'components/common/TextField'
import { isEmpty } from 'lodash-es'
import { webFlowAddress } from 'utils/api/webFlowAddress'
import IconComponent from 'themes/IconComponent'
import { getSupercluster, onClusterClick } from 'utils/mapUtils'
import { geoCode } from 'utils/googleMapsUtils/googleMapsUtils'
import { useFocusOnMobile } from 'hooks/useFocusOnMobile'
import { useStores } from 'hooks/useStores'
import type { NonSavedAddress } from 'mobx/Localization/types'
import { EVENTS } from 'mobx/Localization/constants'

const Container = styled.div`
	width: 600px;
	position: relative;

	@media (max-width: 1024px) {
		width: unset;
		height: 222px;
	}
`

const StyledDivider = styled(Divider)`
	height: 30px;
`

const TextField = styled(TextFieldBase)`
	position: absolute;
	top: 20px;
	width: 80%;
	left: 10%;
	filter: drop-shadow(0px 2px 8px rgba(0, 0, 0, 0.2));

	.MuiInputBase-root {
		height: 45px;
	}

	.MuiInputBase-root {
		padding-inline-end: 0;
	}

	fieldset {
		${({ error }) => (error ? '' : 'border: 0')};
	}

	@media (max-width: 600px) {
		left: unset;
		width: 100%;
		padding: 0 10px;
	}
`

const FindMyLocationIconContainer = styled.div`
	width: 55px;
	height: 45px;
	background-color: var(--primary);
	display: flex;
	justify-content: center;
	align-items: center;
	cursor: pointer;
	border-top-right-radius: var(--borderRadiusInput);
	border-bottom-right-radius: var(--borderRadiusInput);
`

const mapZoomSizes = {
	deepZoom: 13,
	generalZoom: 7,
}

let markerMap
const MAP_DRAGGING_TIMEOUT_IN_MS = 2000

const StoresMap = inject(
	'Home',
	'Infra',
	'MobileApplication',
	'User'
)(
	observer(
		({
			marker,
			selectStoreOnMap,
			MobileApplication,
			Home,
			Infra,
			selectedStoreOnMap,
			selectClusterOnMap,
			selectedOrderType,
			resetStoreOnMap,
			shouldFilterByAvailability,
			setFilterByAvailability,
			User
		}) => {
			const { locale } = Home
			const { addressManagerV2Store } = useStores()
			const [center, setCenter] = useState({})
			const [searchAddressValue, setSearchAddress] = useState(
				Home?.currentLocation?.address || addressManagerV2Store.processingFlow?.address?.formatted || ''
			)
			const [zoom, setZoom] = useState(mapZoomSizes.generalZoom)
			const [bounds, setBounds] = useState([0, 0, 0, 0])
			const [map, setMap] = useState(null)
			const [markers, setMarkers] = useState([])
			const mapDragTimerRef = useRef(null)
			const inputRef = useRef(null)
			const isPopupOpen = addressManagerV2Store.started || (addressManagerV2Store?.addressPopup?.open ?? false)
			const addressTyped = useRef(false)

			useFocusOnMobile(inputRef, !Infra.notification.open && center.lat !== undefined)

			const stores = useMemo(() => {
				// There is an alternative: create in Stores.jsx states for filter truthy and another state filter of nearby = false and merge those 2 and pass to StoresMap.jsx
				const storesListNearby = Home.stores
					.filter((store) => store.availability?.[selectedOrderType]?.availableForAddress)
					.map((store, index) => ({ ...store, storeRank: index }))
				const storesListNonNearby = Home.stores.filter(
					(store) => isEmpty(store.availability[selectedOrderType]) || !store.availability[selectedOrderType]?.availableForAddress
				)
				return [...storesListNearby, ...storesListNonNearby]
			}, [Home.stores, selectedOrderType, zoom])

			const googlePlacesCallback = async (googlePlaceData = {}, fromMyLocation = false) => {
				if (!isEmpty(googlePlaceData) && Object.hasOwn(googlePlaceData, 'name')) {
					googlePlaceData = await getLatLngByAddress(googlePlaceData.name)
				}

				const { l: lang, c: chainId } = Infra.appParams

				const lat = googlePlaceData.lat || googlePlaceData?.geometry?.location.lat()
				const lng = googlePlaceData.lng || googlePlaceData?.geometry?.location.lng()

				const googleMapsObject =
					googlePlaceData.formatted || googlePlaceData.formatted_address
						? googlePlaceData
						: await geoCode(
								{
									location: {
										lat,
										lng,
									},
								},
								locale.key
							)
				const address: NonSavedAddress = {
					formatted: googleMapsObject.formatted || googleMapsObject?.formatted_address,
					location: {
						lat,
						lng,
					},
					components: googleMapsObject?.address_components || [],
				}
				resetStoreOnMap()

				try {
					addressManagerV2Store.processingFlow?.setAddress(address)

					if (addressManagerV2Store.newLocalizationFlowEnabled && fromMyLocation) {
						await addressManagerV2Store.matchAndSetStoreForAddress(address)
					} else {
						const res = await webFlowAddress.getBranchesList({
							formatted: address.formatted,
							lat,
							lng,
							chainId,
							lang,
							orderType: selectedOrderType,
						})

						if (!res.error) {
							Home.setCurrentLocation({ address: address.formatted, lat, lng })
							Home.setStores(res.msg || [])

							if (!Infra?.appParams?.features?.useNewAddressBox) {
								addressManagerV2Store.addressPopup?.setValue('addressObject', googleMapsObject)
							}
							setFilterByAvailability(true)
						}
					}
				} catch (err) {
					console.error(err)
				} finally {
					markerMap.setPosition({
						lat,
						lng,
					})
					setSearchAddress(address.formatted)
					addressManagerV2Store.setAddressPopup({ ...addressManagerV2Store.addressPopup, mapButtonClicked: false })
					addressManagerV2Store.processingFlow?.setOrderTypeFlowDisplayAvailableStores(true)
				}
			}

			const onLocationPermissionGranted = async (location) => {
				setCenter({ lat: location.lat, lng: location.long })
				markerMap.setPosition({ lat: location.lat, lng: location.long })
				await googlePlacesCallback({ lat: location.lat, lng: location.long }, true)
				Infra.setLoading(false)
			}

			useLocationMobileApp(onLocationPermissionGranted)

			useEffect(
				() => () => {
					clearTimeout(mapDragTimerRef.current)
				},
				[]
			)

			useEffect(() => {
				setSearchAddress(Home.currentLocation.address || addressManagerV2Store.processingFlow?.address?.formatted || '')
				setCenter({
					lat: addressManagerV2Store.processingFlow?.address?.location.lat || Home.currentLocation.lat,
					lng: addressManagerV2Store.processingFlow?.address?.location.lng || Home.currentLocation.lng,
				})
				if (markerMap) {
					markerMap.setPosition({
						lat: addressManagerV2Store.processingFlow?.address?.location.lat || Home.currentLocation.lat,
						lng: addressManagerV2Store.processingFlow?.address?.location.lng || Home.currentLocation.lng,
					})
				}
			}, [Home.currentLocation.lat, Home.currentLocation.lng, addressManagerV2Store.processingFlow?.address])

			useEffect(() => {
				if (addressManagerV2Store.addressPopup?.mapButtonClicked || !shouldFilterByAvailability) {
					setZoom(mapZoomSizes.generalZoom)
				} else {
					// setZoom(mapZoomSizes.deepZoom)
				}
			}, [addressManagerV2Store.addressPopup?.mapButtonClicked, shouldFilterByAvailability])

			const supercluster = getSupercluster(stores)

			const handleClusterClick = (cluster) =>
				onClusterClick(cluster, supercluster, (zoomLevel, latLng) => {
					setZoom(zoomLevel)
					setCenter(latLng)
					map?.panTo(latLng)
					selectClusterOnMap(cluster, supercluster)
				})

			const getMarkers = (mapBounds, mapZoom) => {
				const clusters = supercluster.getClusters(mapBounds, mapZoom)
				return clusters.map((cluster) => {
					const [longitude, latitude] = cluster.geometry.coordinates
					const { cluster: isCluster, point_count: pointCount, store } = cluster.properties

					if (isCluster) {
						return (
							<Marker
								key={cluster.id}
								lat={latitude}
								lng={longitude}
								isCluster
								cluster={cluster}
								pointCount={pointCount}
								onClusterClick={() => handleClusterClick(cluster)}
							/>
						)
					}
					return (
						<Marker
							key={store.id}
							onSelectStore={() => selectStoreOnMap(store, store.storeRank + 1)}
							lat={latitude}
							lng={longitude}
							marker={marker}
							distance={store.distance}
							formatted={store.address.formatted}
							rank={store.storeRank + 1}
							isSelected={store.id === selectedStoreOnMap.id}
							isNearby={!!store.availability?.[selectedOrderType]?.availableForAddress}
							isCluster={false}
						/>
					)
				})
			}

			useEffect(() => {
				setMarkers(getMarkers(bounds, zoom))
			}, [selectedStoreOnMap])

			const onGoogleApiLoaded = async (map, maps) => {
				setMap(map)
				markerMap = new maps.Marker({
					map,
					position: {
						lat: addressManagerV2Store.processingFlow?.address?.location.lat || Home.currentLocation.lat,
						lng: addressManagerV2Store.processingFlow?.address?.location.lng || Home.currentLocation.lng,
					},
				})

				const navigatorPermissionState = await getGeoLocationPermissionState()
				if (
					!Infra?.appParams?.features?.blockAutoLocator &&
					navigatorPermissionState !== 'denied' &&
					MobileApplication.userLocationPermissionStatus !== CONSTANTS.MOBILE_APP.PERMISSIONS.LOCATION.DENIED
				) {
					requestMyLocation(onLocationPermissionGranted, isPopupOpen)
				} else {
					const tempFormattedAddress = addressManagerV2Store.getTempFormattedAddressByOrderType(
						User.getOrderType() === CONSTANTS.DELIVERY_METHODS.DELIVERY ? ORDER_TYPES.DELIVERY : ORDER_TYPES.PICKUP
					)

					Home.setCurrentLocation({
						address: tempFormattedAddress || Home.currentLocation.address,
						lat: Home.currentLocation.lat,
						lng: Home.currentLocation.lng,
					})

					inputRef.current.focus()
				}
			}

			const onDrag = (map) => {
				markerMap.setPosition(map.center.toJSON())
			}

			const onDragEnd = () => {
				if (mapDragTimerRef.current) {
					clearTimeout(mapDragTimerRef.current)
				}

				mapDragTimerRef.current = setTimeout(() => {
					const markerLocation = markerMap.getPosition().toJSON()
					googlePlacesCallback(markerLocation)
					setMarkers(getMarkers(bounds, zoom))
				}, MAP_DRAGGING_TIMEOUT_IN_MS)
			}

			const getLatLngByAddress = (address) => {
				const geocoder = new window.google.maps.Geocoder()
				return new Promise((resolve) => {
					geocoder.geocode(
						{
							address,
							componentRestrictions: {
								country: locale.region,
							},
						},
						(responses) => {
							resolve(responses?.[0])
						}
					)
				})
			}

			const onChangeAddress = (e) => {
				setSearchAddress(e.target.value)

				if (e.target.value === '') {
					addressManagerV2Store.processingFlow?.clearAddress()
				}

				if(addressManagerV2Store.started && addressTyped.current === false) {
					addressManagerV2Store.sendLocalizationEvent(EVENTS.TYPED_ADDRESS_MANUALLY)
					addressTyped.current = true
				}
			}

			const onMapChange = ({ zoom, bounds }) => {
				setZoom(zoom)
				const mapBounds = [bounds.nw.lng, bounds.se.lat, bounds.se.lng, bounds.nw.lat]
				setBounds(mapBounds)
				setMarkers(getMarkers(mapBounds, zoom))
			}

			const onCurrentLocationIconClick = () => {
				requestMyLocation(onLocationPermissionGranted, isPopupOpen)
				if(addressManagerV2Store.started) {
					addressManagerV2Store.sendLocalizationEvent(EVENTS.CLICKED_LOCATION_ICON)
				}
			}

			return (
				<Container data-testid="map-container">
					{!isEmpty(locale) && (
						<GoogleMapReact
							yesIWantToUseGoogleMapApiInternals
							onGoogleApiLoaded={({ map, maps }) => onGoogleApiLoaded(map, maps)}
							options={{
								clickableIcons: false,
								fullscreenControl: false,
								gestureHandling: 'greedy',
							}}
							bootstrapURLKeys={{ key: locale?.key || 'AIzaSyBnea1oOS9Wif_l-zV6WfxjA4sZs5kpt-k' }}
							defaultCenter={{ lat: 0, lng: 0 }}
							center={center}
							defaultZoom={mapZoomSizes.deepZoom}
							zoom={zoom}
							onDrag={onDrag}
							onDragEnd={onDragEnd}
							onChange={onMapChange}
						>
							{markers}
						</GoogleMapReact>
					)}

					{/* google-map-react checks if the base apiPayments is already loaded, and if so, uses it, so it won't load a
                    second copy of the library. So the console warning saying "Google maps already loaded" does not
                    request the API from Google's CDN, it's just a warning that this plugin checked that the Google
                    API has already loaded.
                */}

					<GooglePlacesAutoCompleteNew
						{...locale}
						apiKey={locale.key}
						onPlaceSelected={(googlePlaceData) => googlePlacesCallback(googlePlaceData)}
						renderTextField={(ref) => (
							<TextField
								placeholder={getTranslatedTextByKey('webviewFlow.localization.onTheMap.textFieldPlaceholder')}
								inputRef={(node) => {
									ref.current = node
									inputRef.current = node
								}}
								id="localization-page-type-location"
								value={searchAddressValue}
								onChange={onChangeAddress}
								error={
									selectedOrderType === ORDER_TYPES.DELIVERY &&
									Home.currentLocation?.address === '' &&
									!addressManagerV2Store.processingFlow?.address?.formatted
								}
								onResetValue={(value) => {
									setSearchAddress(value)
									Home.setCurrentLocation({ ...Home.currentLocation, address: '' })
									addressManagerV2Store.processingFlow?.clearAddress()
								}}
								inputProps={{ 'data-testid': 'map-dialog-input' }}
								startAdornment={
									<InputAdornment position="start">
										<IconComponent asset="/icons/find.svg" />
									</InputAdornment>
								}
								endAdornment={
									<InputAdornment position="end">
										<StyledDivider orientation="vertical" />
										<FindMyLocationIconContainer
											onClick={onCurrentLocationIconClick}
											id="localization-page-localization-icon"
											data-testid="use-user-location-button"
											role="button"
										>
											<IconComponent asset="/icons/useMyLocation.svg" />
										</FindMyLocationIconContainer>
									</InputAdornment>
								}
							/>
						)}
					/>
				</Container>
			)
		}
	)
)

export default StoresMap
